import { useState } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { ConfirmDialog, Dialog } from '@/ui/Dialog';
import { Field, Input, Select, SelectSuelto, Textarea } from '@/ui/Field';
import { SkeletonTarjetas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import {
  useAreas, useBorrarArea, useGuardarAprobadores, useGuardarArea, useMapaDeAprobacion,
  useRepartoDelArea, useUsuarios, type Area,
} from './api';

/**
 * Áreas — quién manda en cada una, y quién aprueba a quién.
 *
 * El aviso del líder es lo que más importa de esta pantalla y se conserva
 * entero: un área sin líder y con gente dentro deja las solicitudes de esa
 * gente sin nadie que pueda aprobarlas, y un líder **desactivado** hace lo
 * mismo enseñando su nombre como si todo estuviera en orden. Los dos casos se
 * distinguen por texto e ícono, no solo por color.
 */
export function Areas() {
  const consulta = useAreas();
  const borrar = useBorrarArea();
  const [editando, setEditando] = useState<Area | null | undefined>(undefined);
  const [borrando, setBorrando] = useState<Area | null>(null);
  const [viendoMapa, setViendoMapa] = useState(false);

  const areas = consulta.data ?? [];

  return (
    <>
      <div className="mb-3 flex flex-wrap justify-end gap-2">
        <Button variant="secondary" size="sm" onClick={() => setViendoMapa(true)}>
          <i className="bi bi-diagram-3" aria-hidden="true" /> Quién aprueba a quién
        </Button>
        <Button size="sm" onClick={() => setEditando(null)}>
          <i className="bi bi-plus-circle" aria-hidden="true" /> Nueva área
        </Button>
      </div>

      {consulta.isPending ? (
        <SkeletonTarjetas n={6} />
      ) : consulta.isError ? (
        <EstadoError err={consulta.error} que="las áreas" onReintentar={() => void consulta.refetch()} />
      ) : !areas.length ? (
        <EstadoVacio
          icono="bi-building"
          titulo="Sin áreas"
          descripcion="Todo colaborador pertenece a un área, y de ahí sale quién aprueba sus solicitudes."
          accion={{ texto: 'Nueva área', onClick: () => setEditando(null) }}
        />
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {areas.map((a) => (
            <li key={a.id} className="flex">
              <Card className="flex w-full flex-col gap-1 p-3">
                <div className="flex items-start justify-between gap-2">
                  <i className="bi bi-building text-7 text-brand-ink" aria-hidden="true" />
                  <div className="flex gap-1">
                    <Button
                      variant="ghost" size="sm" iconOnly
                      aria-label={`Editar ${a.name}`}
                      onClick={() => setEditando(a)}
                    >
                      <i className="bi bi-pencil" aria-hidden="true" />
                    </Button>
                    <Button
                      variant="ghost" size="sm" iconOnly
                      aria-label={`Eliminar ${a.name}`}
                      disabled={a.user_count > 0}
                      razonDeshabilitado={a.user_count > 0
                        ? `Tiene ${a.user_count} colaborador${a.user_count === 1 ? '' : 'es'}: reasígnalos primero`
                        : undefined}
                      onClick={() => setBorrando(a)}
                    >
                      <i className="bi bi-trash text-danger-ink" aria-hidden="true" />
                    </Button>
                  </div>
                </div>
                <strong className="text-3 font-bold text-brand-ink">{a.name}</strong>
                {a.description && <p className="text-2 text-muted">{a.description}</p>}
                <p className="text-2 text-muted">
                  <i className="bi bi-people" aria-hidden="true" /> {a.user_count}{' '}
                  {a.user_count === 1 ? 'colaborador' : 'colaboradores'}
                </p>
                <AvisoDeLider area={a} />
              </Card>
            </li>
          ))}
        </ul>
      )}

      {editando !== undefined && (
        <FormularioDeArea area={editando} onCerrar={() => setEditando(undefined)} />
      )}

      {viendoMapa && <MapaDeAprobacion onCerrar={() => setViendoMapa(false)} />}

      <ConfirmDialog
        abierto={borrando != null}
        onCambio={(v) => { if (!v) setBorrando(null); }}
        titulo="Eliminar el área"
        descripcion={`«${borrando?.name ?? ''}» deja de existir. No se puede deshacer.`}
        textoConfirmar="Eliminar"
        destructivo
        cargando={borrar.isPending}
        onConfirmar={() => {
          if (!borrando) return;
          borrar.mutate(borrando.id, {
            onSuccess: () => { Utils.toast('Área eliminada', 'success'); setBorrando(null); },
            onError: (err) => {
              setBorrando(null);
              Utils.toast(err instanceof Error ? err.message : 'No se pudo eliminar', 'error');
            },
          });
        }}
      />
    </>
  );
}

/* ── El estado del líder, dicho en voz alta ────────────────────────────── */

function Linea({ grave, icono, children }: {
  grave?: boolean;
  icono: string;
  children: React.ReactNode;
}) {
  return (
    <p className={cn(
      'mt-1 flex items-start gap-1.5 text-2 leading-snug',
      grave
        ? 'rounded-sm border border-warn/40 bg-warn/16 p-2 font-semibold text-warn-ink'
        : 'text-ink-2',
    )}>
      <i className={`bi ${icono} mt-0.5 shrink-0`} aria-hidden="true" />
      <span className="min-w-0">{children}</span>
    </p>
  );
}

function AvisoDeLider({ area: a }: { area: Area }) {
  const filas = a.approvers ?? [];

  // Se agrupa por persona para que se lea «Ana Ruiz · SAT, SSE» y no tres
  // renglones con el mismo nombre.
  const porPersona = new Map<number, { nombre: string; activo: boolean; empresas: string[] }>();
  for (const f of filas) {
    if (!porPersona.has(f.user_id)) {
      porPersona.set(f.user_id, {
        nombre: f.user_name, activo: f.user_active !== false, empresas: [],
      });
    }
    porPersona.get(f.user_id)!.empresas.push(f.company_name || 'el resto');
  }

  return (
    <>
      {[...porPersona.values()].map((p) => (
        <Linea key={p.nombre} grave={!p.activo} icono={p.activo ? 'bi-person-badge' : 'bi-exclamation-triangle-fill'}>
          {p.nombre} · {p.empresas.join(', ')}
          {!p.activo && <> — <strong>desactivado</strong>, esas empresas quedan sin quién apruebe</>}
        </Linea>
      ))}

      {!filas.length && (
        a.manager_name ? (
          a.manager_active === false ? (
            <Linea grave icono="bi-exclamation-triangle-fill">
              Líder: {a.manager_name} — <strong>desactivado</strong>, ya no puede aprobar
            </Linea>
          ) : (
            <Linea icono="bi-person-badge">Líder: {a.manager_name}</Linea>
          )
        ) : (
          <Linea
            grave={a.user_count > 0}
            icono={a.user_count > 0 ? 'bi-exclamation-triangle-fill' : 'bi-person-dash'}
          >
            Sin líder asignado
            {a.user_count > 0 && (
              <> — las solicitudes de sus {a.user_count} colaborador
                {a.user_count !== 1 ? 'es' : ''} no tendrán quién las apruebe</>
            )}
          </Linea>
        )
      )}

      {!!filas.length && a.manager_name && a.manager_active !== false && (
        <Linea icono="bi-shield-check">Respaldo: {a.manager_name}</Linea>
      )}

      {!!a.sin_cubrir?.length && (
        <Linea grave icono="bi-exclamation-triangle-fill">
          Sin cubrir: <strong>{a.sin_cubrir.join(', ')}</strong> — esos colaboradores no tienen
          quién les apruebe
        </Linea>
      )}
    </>
  );
}

/* ── El formulario ─────────────────────────────────────────────────────── */

function FormularioDeArea({ area, onCerrar }: { area: Area | null; onCerrar: () => void }) {
  const guardar = useGuardarArea();
  const guardarAprobadores = useGuardarAprobadores();
  const usuarios = useUsuarios('', 'activos', true);
  const reparto = useRepartoDelArea(area?.id ?? null);

  const [nombre, setNombre] = useState(area?.name ?? '');
  const [descripcion, setDescripcion] = useState(area?.description ?? '');
  const [lider, setLider] = useState<number | null>(area?.manager_user_id ?? null);
  const [filas, setFilas] = useState<Array<{ user_id: number | null; company_id: number | null }>>([]);
  const [error, setError] = useState('');
  const [cargadas, setCargadas] = useState(false);

  if (reparto.data && !cargadas) {
    setFilas(reparto.data.approvers.map((x) => ({ ...x })));
    setCargadas(true);
  }

  const gente = usuarios.data?.items ?? [];
  const empresas = reparto.data?.empresas ?? [];

  return (
    <Dialog
      abierto
      onCambio={(v) => { if (!v && !guardar.isPending) onCerrar(); }}
      titulo={area ? 'Editar el área' : 'Nueva área'}
      descripcion="El líder aprueba el paso «Líder de área», y hace de respaldo del paso «Jefe inmediato» para quien no tenga jefe propio."
      ancho="max-w-2xl"
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button
            loading={guardar.isPending}
            onClick={() => {
              if (!nombre.trim()) { setError('El nombre es obligatorio.'); return; }
              guardar.mutate(
                {
                  id: area?.id ?? null,
                  cuerpo: {
                    name: nombre.trim(),
                    description: descripcion.trim() || null,
                    manager_user_id: lider,
                  },
                },
                {
                  onSuccess: async () => {
                    if (area) {
                      try { await guardarAprobadores.mutateAsync({ areaId: area.id, filas }); }
                      catch (err) {
                        Utils.toast(err instanceof Error
                          ? `Área guardada, pero el reparto no: ${err.message}`
                          : 'Área guardada, pero el reparto no', 'warning');
                      }
                    }
                    Utils.toast(area ? 'Área actualizada' : 'Área creada', 'success');
                    onCerrar();
                  },
                  onError: (err) => setError(
                    err instanceof Error ? err.message : 'No se pudo guardar'),
                },
              );
            }}
          >
            Guardar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4 pb-2">
        <Field label="Nombre" error={error || undefined}>
          <Input
            value={nombre}
            onChange={(e) => { setNombre(e.target.value); setError(''); }}
            placeholder="Ej: Operaciones"
          />
        </Field>
        <Field label="Descripción" opcional>
          <Textarea rows={2} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
        </Field>
        <Field label="Líder del área" opcional>
          <Select
            value={lider == null ? '' : String(lider)}
            onChange={(e) => setLider(Number(e.target.value) || null)}
          >
            <option value="">— Sin responsable —</option>
            {gente.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </Select>
        </Field>

        {area && (
          <fieldset>
            <legend className="flex items-center gap-2 text-3 font-semibold text-ink">
              <i className="bi bi-diagram-2" aria-hidden="true" /> Reparto por empresa
            </legend>
            <p className="mb-2 text-2 text-muted">
              Un área puede reunir gente de varias empresas del grupo. <strong>Lo específico
              manda</strong>: si alguien cubre SAT, las solicitudes de SAT van a esa persona y no
              al resto. Con dos personas en la misma empresa, basta que una apruebe.
            </p>
            <ul className="flex flex-col gap-1.5">
              {filas.map((f, i) => (
                <li key={i} className="flex flex-wrap items-center gap-1.5">
                  <SelectSuelto
                    aria-label={`Persona del reparto ${i + 1}`}
                    className="min-w-40 flex-1 py-1 text-2"
                    value={f.user_id == null ? '' : String(f.user_id)}
                    onChange={(e) => setFilas((s) => s.map((x, j) => (j === i
                      ? { ...x, user_id: Number(e.target.value) || null } : x)))}
                  >
                    <option value="">— Elegir persona —</option>
                    {gente.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                  </SelectSuelto>
                  <i className="bi bi-arrow-right shrink-0 text-muted" aria-hidden="true" />
                  <SelectSuelto
                    aria-label={`Empresa del reparto ${i + 1}`}
                    className="min-w-40 flex-1 py-1 text-2"
                    value={f.company_id == null ? '' : String(f.company_id)}
                    onChange={(e) => setFilas((s) => s.map((x, j) => (j === i
                      ? { ...x, company_id: Number(e.target.value) || null } : x)))}
                  >
                    <option value="">Todas las demás</option>
                    {empresas.map((e2) => (
                      <option key={e2.id} value={e2.id}>
                        {e2.name}
                        {e2.en_area ? ` (${e2.en_area})` : ''}
                        {e2.activa === false ? ' — inactiva' : ''}
                      </option>
                    ))}
                  </SelectSuelto>
                  <Button
                    variant="ghost" size="sm" iconOnly
                    aria-label={`Quitar la fila ${i + 1} del reparto`}
                    onClick={() => setFilas((s) => s.filter((_, j) => j !== i))}
                  >
                    <i className="bi bi-x-lg text-danger-ink" aria-hidden="true" />
                  </Button>
                </li>
              ))}
            </ul>
            {!filas.length && (
              <p className="text-2 text-muted">
                Sin reparto: todas las solicitudes del área irán al líder de arriba.
              </p>
            )}
            <Button
              variant="secondary"
              size="sm"
              className="mt-2"
              onClick={() => setFilas((s) => [...s, { user_id: null, company_id: null }])}
            >
              <i className="bi bi-plus-lg" aria-hidden="true" /> Agregar líder
            </Button>
          </fieldset>
        )}
      </div>
    </Dialog>
  );
}

/* ── El mapa ───────────────────────────────────────────────────────────── */

function MapaDeAprobacion({ onCerrar }: { onCerrar: () => void }) {
  const consulta = useMapaDeAprobacion(null, true);
  const d = consulta.data;

  return (
    <Dialog
      abierto
      onCambio={(v) => { if (!v) onCerrar(); }}
      titulo="Quién aprueba a quién"
      descripcion="Responde de una vez lo que antes obligaba a abrir las fichas una por una."
      ancho="max-w-4xl"
      pie={<Button onClick={onCerrar}>Cerrar</Button>}
    >
      <div className="pb-2">
        {consulta.isPending ? (
          <p className="text-3 text-muted" aria-busy="true">Calculando…</p>
        ) : consulta.isError ? (
          <EstadoError
            err={consulta.error}
            que="el mapa de aprobación"
            onReintentar={() => void consulta.refetch()}
          />
        ) : !d ? null : (
          <>
            <p className={cn(
              'mb-3 rounded-sm border p-3 text-3',
              d.sin_aprobador
                ? 'border-danger/40 bg-danger/12 text-danger-ink'
                : 'border-ok/40 bg-ok/12 text-ink-2',
            )}>
              {d.sin_aprobador ? (
                <>
                  <strong>
                    <i className="bi bi-exclamation-triangle" aria-hidden="true" />{' '}
                    {d.sin_aprobador} de {d.total} colaborador{d.total !== 1 ? 'es' : ''} no tiene
                    quién le apruebe
                  </strong>
                  <span className="mt-1 block">
                    Sus solicitudes quedarán represadas: solo un administrador podría destrabarlas.
                  </span>
                </>
              ) : (
                <>
                  <i className="bi bi-check2-circle" aria-hidden="true" /> Los {d.total}{' '}
                  colaboradores activos tienen aprobador asignado.
                </>
              )}
            </p>

            <div className="max-h-[55vh] overflow-auto rounded-sm border border-line">
              <table className="w-full border-collapse text-2">
                <thead className="sticky top-0 bg-sunken">
                  <tr className="border-b border-line text-left text-muted">
                    {['Colaborador', 'Área', 'Empresa', 'Aprueba(n)', 'Vía'].map((h) => (
                      <th key={h} scope="col" className="px-3 py-2 font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {d.filas.map((f, i) => (
                    <tr
                      key={i}
                      className={cn('border-b border-line last:border-0',
                        f.via === 'NADIE' && 'bg-danger/8')}
                    >
                      <td className="px-3 py-1.5 text-ink">{f.nombre}</td>
                      <td className="px-3 py-1.5 text-muted">{f.area}</td>
                      <td className="px-3 py-1.5 text-muted">{f.empresa}</td>
                      <td className="px-3 py-1.5">
                        {f.aprobadores.length ? f.aprobadores.join(', ') : (
                          <span className="font-semibold text-danger-ink">
                            <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />{' '}
                            Sin aprobador
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-muted">{f.via}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}
